import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LuCalendarClock, LuInfo, LuPencil, LuPlay, LuRefreshCw, LuTrash2, LuUsers, LuX } from "react-icons/lu";

import { pokerApi } from "../../api/pokerApi";
import { showToast } from "../../utils/toast/Toast";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import Card from "../ui/Card";
import Tooltip from "../ui/Tooltip";
import { useTournamentChannel } from "./hooks/useTournamentChannel";
import TournamentInfoPanel from "./TournamentInfoPanel";

// Single Table has no tournament-level WS channel the way MTT has
// TournamentChannelConsumer (the only WS Single Table ever gets is the actual
// poker table's /ws/game/<id>/, which doesn't exist until in_progress, and
// even then is gameplay state, not this panel's registering/pre-start info).
// So this panel refreshes via periodic re-fetch while expanded, for BOTH
// types -- "live" here means "polls while open," not WS-pushed. MTT already
// has its own separate WS channel (useTournamentChannel, wired for the
// card's CTA/status badge above), but this panel deliberately doesn't also
// consume it -- mixing a push-updated source with a polled one for the same
// fields would need its own precedence rules for no real benefit at a
// 20-second poll cadence.
const INFO_PANEL_POLL_MS = 20000;

function useDetailPolling(fetchFn, enabled) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchFn();
        if (!cancelled) setData(result);
      } catch {
        // Best-effort -- the panel just keeps showing the last known data
        // (or its own loading state, on the very first failed fetch) rather
        // than surfacing a toast for a background refresh.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const interval = setInterval(load, INFO_PANEL_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return { data, loading };
}

// Mirrors the backend's EDIT_LOCK_MINUTES_BEFORE_START
// (poker/serializers/scheduled_match_serializers.py) -- Single Table locks
// editing this close to its scheduled_start even while status is still
// "open" (registration never technically closed). This is a live comparison
// against Date.now() at render time, not just a status check -- the whole
// point of the 15-minute window.
const EDIT_LOCK_MINUTES_BEFORE_START = 15;

function statusVariant(status) {
  if (status === "open") return "success";
  if (status === "closed") return "warning";
  if (status === "in_progress") return "brand";
  if (status === "cancelled") return "danger";
  return "neutral";
}

function formatStart(value) {
  if (!value) return "Unscheduled";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (!data) return fallback;
  if (typeof data.error === "string") return data.error;
  const first = Object.values(data).flat()[0];
  return typeof first === "string" ? first : fallback;
}

export default function PokerTournamentsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "mtt" ? "mtt" : "scheduled";
  const [tournaments, setTournaments] = useState([]);
  const [mttTournaments, setMttTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mttLoading, setMttLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rosterTournamentId, setRosterTournamentId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name } | null
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setMttLoading(true);
    try {
      const [data, mttData] = await Promise.all([
        pokerApi.listTournaments(),
        pokerApi.loadMttTournamentsForDashboard(),
      ]);
      setTournaments(data?.results || []);
      setMttTournaments(mttData || []);
    } catch {
      showToast("error", "Could not load tournaments.");
    } finally {
      setLoading(false);
      setMttLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upcoming = useMemo(
    () => [...tournaments].sort((a, b) => new Date(a.scheduled_start) - new Date(b.scheduled_start)),
    [tournaments]
  );
  const rosterTournament = useMemo(
    () => upcoming.find((tournament) => tournament.id === rosterTournamentId) || null,
    [rosterTournamentId, upcoming]
  );

  const setTab = (tab) => {
    setSearchParams(tab === "mtt" ? { tab: "mtt" } : {});
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await pokerApi.deleteTournament(deleteTarget.id);
      showToast("success", "Tournament deleted.");
      setDeleteTarget(null);
      await load();
    } catch (error) {
      showToast("error", errorMessage(error, "Could not delete tournament."));
    } finally {
      setDeleting(false);
    }
  };

  const runAction = async (tournamentId, action, successMessage) => {
    setBusyId(tournamentId);
    try {
      const result = await action();
      if (successMessage) showToast("success", successMessage);
      if (result?.gameId) {
        navigate(`/games/poker/${result.gameId}`);
        return;
      }
      await load();
    } catch (error) {
      showToast("error", errorMessage(error, "Tournament action failed."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="w-full px-1 pb-4 pt-1 sm:px-4 sm:pb-24 sm:pt-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="hidden text-[11px] uppercase tracking-[0.28em] text-text-muted sm:block">
              Poker
            </div>
            <h1 className="font-['Space_Grotesk',Inter,sans-serif] text-xl font-semibold tracking-wide text-text-primary sm:text-2xl">
              Tournaments
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 sm:justify-end">
            <div className="grid grid-cols-2 rounded-button border border-border-soft bg-slate-950/40 p-1">
              <TabButton active={activeTab === "scheduled"} onClick={() => setTab("scheduled")}>
                Single Table
              </TabButton>
              <TabButton active={activeTab === "mtt"} onClick={() => setTab("mtt")}>
                Multi-Table
              </TabButton>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={load} disabled={loading}>
              <LuRefreshCw size={15} />
              Refresh
            </Button>
            <Button type="button" size="sm" onClick={() => navigate("/tournaments/create")}>
              Create Tournament
            </Button>
          </div>
        </div>

        {activeTab === "scheduled" ? (
          <div className="grid gap-3 lg:grid-cols-2">
            {loading ? (
            <Card variant="outline" className="grid min-h-[220px] place-items-center p-4 text-sm text-text-secondary lg:col-span-2">
              Loading tournaments...
            </Card>
            ) : null}

            {!loading && upcoming.length === 0 ? (
            <Card variant="outline" className="grid min-h-[220px] place-items-center p-6 text-center text-sm text-text-faint lg:col-span-2">
              No tournaments posted yet.
            </Card>
            ) : null}

            {!loading && upcoming.map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament}
              busy={busyId === tournament.id}
              onRegister={() => runAction(
                tournament.id,
                () => pokerApi.registerTournament(tournament.id),
                "Registered."
              )}
              onWithdraw={() => runAction(
                tournament.id,
                () => pokerApi.withdrawTournament(tournament.id),
                "Registration withdrawn."
              )}
              onStart={() => runAction(
                tournament.id,
                () => pokerApi.startTournament(tournament.id),
                "Tournament started."
              )}
              onRoster={() => setRosterTournamentId(tournament.id)}
              onJoin={() => navigate(`/games/poker/${tournament.game_id}`)}
              onDelete={() => setDeleteTarget({ id: tournament.id, name: tournament.title })}
              onEdit={() => navigate(`/tournaments/edit/single/${tournament.id}`)}
            />
            ))}
          </div>
        ) : (
          <MttTab loading={mttLoading} tournaments={mttTournaments} onChanged={load} />
        )}
      </div>

      {rosterTournament ? (
        <RosterModal
          tournament={rosterTournament}
          busy={busyId === rosterTournament.id}
          onClose={() => setRosterTournamentId(null)}
          onRemove={(registrationId) => runAction(
            rosterTournament.id,
            () => pokerApi.removeTournamentRegistration(rosterTournament.id, registrationId),
            "Player removed."
          )}
        />
      ) : null}

      {deleteTarget ? (
        <ConfirmDeleteModal
          name={deleteTarget.name}
          busy={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      ) : null}

    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 rounded-button px-3 text-xs font-semibold uppercase tracking-[0.12em] transition ${
        active
          ? "bg-brand-cyan/10 text-brand-cyan"
          : "text-text-muted hover:bg-surface hover:text-text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function MttTab({ loading, tournaments, onChanged }) {
  if (loading) {
    return (
      <Card variant="outline" className="grid min-h-[220px] place-items-center p-4 text-sm text-text-secondary">
        Loading tournaments...
      </Card>
    );
  }

  if (tournaments.length === 0) {
    return (
      <Card variant="outline" className="grid min-h-[220px] place-items-center p-6 text-center text-sm text-text-faint">
        No multi-table tournaments open right now.
      </Card>
    );
  }

  return (
    <div className="grid gap-3 lg:grid-cols-2">
      {tournaments.map((tournament) => (
        <MttTournamentCard key={tournament.id} tournament={tournament} onChanged={onChanged} />
      ))}
    </div>
  );
}

// Normalizes an MTT detail-GET response (pokerApi.getMttTournament) into
// TournamentInfoPanel's shared shape. current_level is null pre-start
// (TournamentSerializer.get_current_level suppresses it until status is
// "running", same as the list serializer) -- fall back to blind_schedule[0]
// for the starting numbers so the panel shows something meaningful before
// the clock has actually started, with levelEmptyMessage making clear that's
// a "not started yet" state, not "this tournament has flat blinds" (MTT
// always has a real schedule; only Single Table can genuinely be flat).
function normalizeMttInfo(data) {
  const firstLevel = data.blind_schedule?.[0];
  return {
    statusLabel: data.status,
    startingStack: data.starting_stack,
    registeredCount: data.registered_count,
    maxEntrants: data.max_entrants,
    eliminatedCount: data.eliminated_count,
    tableCount: data.active_table_count,
    currentSmallBlind: data.current_level?.small_blind ?? firstLevel?.sb,
    currentBigBlind: data.current_level?.big_blind ?? firstLevel?.bb,
    currentAnte: data.current_level?.ante ?? firstLevel?.ante ?? null,
    levelSecondsRemaining: data.current_level?.seconds_remaining ?? null,
    levelEmptyMessage: "Increases once the tournament starts",
    breakStatus: data.break_status,
    scheduledStartLabel: null,
    isPastScheduledStart: null,
  };
}

// The list endpoint (GET /api/poker/mtt/) is tournament-scoped, not
// user-scoped -- it has no idea whether the logged-in user is registered,
// let alone seated (is_creator is the one deliberate exception, added for the
// Delete action below). So each card independently fetches its own my-entry/
// status on mount, then layers the tournament-wide WebSocket channel on top
// ONLY once an entry is confirmed to exist (the channel consumer rejects with
// 4003 otherwise, so there's no point attempting it for a tournament the user
// was never part of).
function MttTournamentCard({ tournament, onChanged }) {
  const navigate = useNavigate();
  const [entryState, setEntryState] = useState("loading"); // "loading" | "none" | "loaded" | "error"
  const [entry, setEntry] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const { data: infoData, loading: infoLoading } = useDetailPolling(
    () => pokerApi.getMttTournament(tournament.id),
    showInfo
  );

  const loadEntry = useCallback(async () => {
    try {
      const data = await pokerApi.getMttEntry(tournament.id);
      setEntry(data);
      setEntryState("loaded");
    } catch (error) {
      if (error?.response?.status === 404) {
        setEntryState("none");
      } else {
        setEntryState("error");
      }
    }
  }, [tournament.id]);

  useEffect(() => {
    loadEntry();
  }, [loadEntry]);

  const hasEntry = entryState === "loaded";
  const channel = useTournamentChannel(tournament.id, { enabled: hasEntry });

  // Once the channel has delivered its own snapshot, it's the live source of
  // truth (it's the same tournament_state_payload shape the my-entry/ REST
  // call's fields map onto); until then, fall back to the initial REST fetch
  // so the card isn't blank while the socket is still connecting.
  const live = channel.wsStatus === "connected" || channel.wsStatus === "connecting";
  const myStatus = live && channel.myStatus !== null ? channel.myStatus : entry?.status ?? null;
  const myFinishPosition = live && channel.myStatus !== null ? channel.myFinishPosition : entry?.finish_position ?? null;
  const gameId = live && channel.myStatus !== null ? channel.gameId : entry?.game_id ?? null;
  const tournamentStatus = (live && channel.tournamentStatus) || tournament.status;
  const registeredCount = tournament.registered_count;
  const maxEntrants = tournament.max_entrants;
  const isRunning = tournamentStatus === "running";

  // Two mismatches normalized here, not just a field-naming one: the REST
  // list payload (tournament.current_level) is snake_case and already null
  // pre-start (TournamentListSerializer suppresses it deliberately); the WS
  // channel's tournament_state snapshot is camelCase AND -- found via actual
  // browser testing, not assumed -- always carries level-1's numbers as a
  // preview even before the tournament starts (get_current_level() returns
  // the checkpoint level's numbers whenever level_started_at is None, not
  // just once running). Gating on isRunning here keeps "blinds only shown
  // once running" true regardless of which source supplied the data.
  const rawLevel = isRunning ? (live && channel.level) || tournament.current_level : null;
  const level = rawLevel
    ? {
        smallBlind: rawLevel.small_blind ?? rawLevel.smallBlind,
        bigBlind: rawLevel.big_blind ?? rawLevel.bigBlind,
      }
    : null;

  const cta = (() => {
    if (entryState === "none") return { label: "Register", kind: "register" };
    if (entryState !== "loaded") return null; // still loading / errored -- no action to offer yet
    if (myStatus === "eliminated") return { label: "Eliminated", kind: "eliminated" };
    if (isRunning && gameId) return { label: "Join Table", kind: "join" };
    if (isRunning) return { label: "View Status", kind: "status" };
    return { label: "Registered", kind: "registered" };
  })();

  const handleCta = async () => {
    if (!cta) return;
    if (cta.kind === "join") {
      navigate(`/games/poker/${gameId}`);
      return;
    }
    if (cta.kind !== "register") return; // Registered / Eliminated / View Status are non-actionable
    setRegistering(true);
    try {
      await pokerApi.registerMtt(tournament.id);
      showToast("success", "Registered.");
      await loadEntry();
      onChanged?.();
    } catch (error) {
      showToast("error", errorMessage(error, "Could not register."));
    } finally {
      setRegistering(false);
    }
  };

  const ctaDisabled = !cta || cta.kind === "registered" || cta.kind === "status" || cta.kind === "eliminated" || registering;

  // Pre-start only -- matches the backend's own delete_if_not_started() guard,
  // which rejects once status leaves "registering". Edit shares the exact same
  // lock (status-based only -- MTT has no scheduled_start, so unlike Single
  // Table there's no additional time-based component here).
  const canDelete = tournament.is_creator && tournamentStatus === "registering";
  const canEdit = canDelete;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await pokerApi.deleteMttTournament(tournament.id);
      showToast("success", "Tournament deleted.");
      setConfirmingDelete(false);
      onChanged?.();
    } catch (error) {
      showToast("error", errorMessage(error, "Could not delete tournament."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card variant="glass" className="flex min-h-0 flex-col p-3 sm:min-h-[220px] sm:p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Badge variant="warning" className="mb-2 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em]">
            Multi-Table
          </Badge>
          <div className="truncate text-base font-semibold text-text-primary">{tournament.name}</div>
        </div>
        <Badge variant={isRunning ? "success" : "warning"} className="capitalize">
          {tournamentStatus}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-text-secondary">
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          <LuUsers className="mb-1 text-brand-emerald" size={16} />
          {registeredCount}/{maxEntrants}
        </div>
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          Blinds<br />
          <span className="text-text-primary">
            {level ? `${level.smallBlind}/${level.bigBlind}` : "—"}
          </span>
        </div>
      </div>

      {myFinishPosition ? (
        <div className="mt-2 text-[11px] text-text-muted">Finish position: #{myFinishPosition}</div>
      ) : null}

      <div className="mt-4 flex flex-wrap gap-2">
        {cta ? (
          <Button
            type="button"
            size="sm"
            className="h-11 w-full sm:h-9 sm:w-auto"
            variant={cta.kind === "registered" || cta.kind === "status" || cta.kind === "eliminated" ? "outline" : "primary"}
            onClick={handleCta}
            disabled={ctaDisabled}
          >
            {cta.label}
          </Button>
        ) : null}
        {canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 sm:h-9"
            onClick={() => navigate(`/tournaments/edit/mtt/${tournament.id}`)}
          >
            <LuPencil size={15} />
            Edit
          </Button>
        ) : null}
        {canDelete ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 border-brand-rose/30 text-brand-rose hover:bg-brand-rose/10 sm:h-9"
            onClick={() => setConfirmingDelete(true)}
          >
            <LuTrash2 size={15} />
            Delete
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 sm:h-9"
          onClick={() => setShowInfo((v) => !v)}
        >
          <LuInfo size={15} />
          {showInfo ? "Hide Details" : "Details"}
        </Button>
      </div>

      {showInfo ? (
        <TournamentInfoPanel data={infoData ? normalizeMttInfo(infoData) : null} loading={infoLoading} />
      ) : null}

      {confirmingDelete ? (
        <ConfirmDeleteModal
          name={tournament.name}
          busy={deleting}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={handleDelete}
        />
      ) : null}
    </Card>
  );
}

// Normalizes a Single Table detail-GET response (pokerApi.getTournament) into
// TournamentInfoPanel's shared shape. Single Table has neither an elimination
// concept (registrations only ever go registered/withdrawn/removed -- see
// ScheduledPokerMatchRegistration.STATUS_CHOICES, nothing tracks anything
// post-start) nor a break schedule, so eliminatedCount/tableCount/breakStatus
// are all null -- not omitted for lack of data, genuinely not a concept that
// applies to this type.
function normalizeSingleTableInfo(data) {
  const isPast = new Date(data.scheduled_start).getTime() <= Date.now();
  const hasSchedule = Boolean(data.level_duration_minutes);
  return {
    statusLabel: data.status.replace("_", " "),
    startingStack: data.starting_chips,
    registeredCount: data.registered_count,
    maxEntrants: data.max_players,
    eliminatedCount: null,
    tableCount: null,
    currentSmallBlind: data.current_level.small_blind,
    currentBigBlind: data.current_level.big_blind,
    currentAnte: null,
    levelSecondsRemaining: data.current_level.seconds_remaining,
    levelEmptyMessage: hasSchedule
      ? "Increases once the table starts"
      : "Flat blinds -- no scheduled increases",
    breakStatus: null,
    scheduledStartLabel: formatStart(data.scheduled_start),
    isPastScheduledStart: isPast,
  };
}

function TournamentCard({
  tournament,
  busy,
  onRegister,
  onWithdraw,
  onStart,
  onRoster,
  onJoin,
  onDelete,
  onEdit,
}) {
  const [showInfo, setShowInfo] = useState(false);
  const { data: infoData, loading: infoLoading } = useDetailPolling(
    () => pokerApi.getTournament(tournament.id),
    showInfo
  );
  const canRegister = tournament.status === "open" && !tournament.is_registered;
  const canWithdraw = tournament.is_registered && ["open", "closed"].includes(tournament.status) && !tournament.is_creator;
  const canStart = tournament.is_creator && ["open", "closed"].includes(tournament.status);
  const canJoin = tournament.status === "in_progress" && tournament.game_id && tournament.is_registered;
  // Pre-start only (same statuses as canStart) -- matches the backend's own
  // delete_if_not_started() guard, which rejects once status is in_progress.
  const canDelete = tournament.is_creator && ["open", "closed"].includes(tournament.status);
  // Edit additionally locks within 15 minutes of scheduled_start even while
  // status is still "open" -- a live Date.now() comparison, not just the
  // status check canDelete/canStart use, matching the backend's
  // ScheduledPokerMatchEditSerializer.update() guard.
  const canEdit =
    canDelete &&
    Date.now() < new Date(tournament.scheduled_start).getTime() - EDIT_LOCK_MINUTES_BEFORE_START * 60 * 1000;

  return (
    <Card variant="glass" className="flex min-h-0 flex-col p-3 sm:min-h-[260px] sm:p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-base font-semibold text-text-primary">{tournament.title}</div>
          <div className="mt-1 text-xs text-text-secondary">Hosted by {tournament.creator_name}</div>
        </div>
        <Badge variant={statusVariant(tournament.status)} className="capitalize">
          {tournament.status.replace("_", " ")}
        </Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-text-secondary sm:grid-cols-4">
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          <LuCalendarClock className="mb-1 text-brand-cyan" size={16} />
          {formatStart(tournament.scheduled_start)}
        </div>
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          <LuUsers className="mb-1 text-brand-emerald" size={16} />
          {tournament.registered_count}/{tournament.max_players}
        </div>
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          Stack<br />
          <span className="text-text-primary">{tournament.starting_chips}</span>
        </div>
        <div className="rounded-lg border border-border-soft bg-slate-950/40 p-2">
          Blinds<br />
          <span className="text-text-primary">{tournament.small_blind}/{tournament.big_blind}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
        <Button type="button" variant="outline" size="sm" className="h-11 sm:h-9" onClick={onRoster}>
          <LuUsers size={15} />
          Roster
        </Button>
        {canRegister ? (
          <Button type="button" size="sm" className="h-11 sm:h-9" onClick={onRegister} disabled={busy}>
            Register
          </Button>
        ) : null}
        {canWithdraw ? (
          <Button type="button" variant="outline" size="sm" className="h-11 sm:h-9" onClick={onWithdraw} disabled={busy}>
            Withdraw
          </Button>
        ) : null}
        {canStart ? (
          <Button type="button" variant="secondary" size="sm" className="h-11 sm:h-9" onClick={onStart} disabled={busy}>
            <LuPlay size={15} />
            Start
          </Button>
        ) : null}
        {canJoin ? (
          <Button type="button" size="sm" className="h-11 sm:h-9" onClick={onJoin}>
            Join Table
          </Button>
        ) : null}
        {canEdit ? (
          <Button type="button" variant="outline" size="sm" className="h-11 sm:h-9" onClick={onEdit} disabled={busy}>
            <LuPencil size={15} />
            Edit
          </Button>
        ) : null}
        {canDelete ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-11 border-brand-rose/30 text-brand-rose hover:bg-brand-rose/10 sm:h-9"
            onClick={onDelete}
            disabled={busy}
          >
            <LuTrash2 size={15} />
            Delete
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-11 sm:h-9"
          onClick={() => setShowInfo((v) => !v)}
        >
          <LuInfo size={15} />
          {showInfo ? "Hide Details" : "Details"}
        </Button>
      </div>

      {showInfo ? (
        <TournamentInfoPanel
          data={infoData ? normalizeSingleTableInfo(infoData) : null}
          loading={infoLoading}
        />
      ) : null}
    </Card>
  );
}

function ConfirmDeleteModal({ name, busy, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/70 px-0 py-0 backdrop-blur-sm sm:items-center sm:px-3 sm:py-6">
      <div className="w-full max-w-sm rounded-t-2xl border border-border-soft bg-background-app-panel p-4 shadow-[0_28px_90px_rgba(0,0,0,0.55)] sm:rounded-lg">
        <div className="text-base font-semibold text-text-primary">Delete tournament?</div>
        <div className="mt-2 text-sm text-text-secondary">
          This permanently deletes <span className="font-semibold text-text-primary">{name}</span> and every
          registration in it. This can't be undone.
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" className="h-10" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-10 border border-brand-rose/40 bg-brand-rose/15 text-brand-rose hover:bg-brand-rose/25"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function RosterModal({ tournament, busy, onClose, onRemove }) {
  const registrations = tournament.registrations || [];
  const canManage = tournament.is_creator && ["open", "closed"].includes(tournament.status);

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/70 px-0 py-0 backdrop-blur-sm sm:items-center sm:px-3 sm:py-6">
      <div className="w-full max-w-md rounded-t-2xl border border-border-soft bg-background-app-panel shadow-[0_28px_90px_rgba(0,0,0,0.55)] sm:rounded-lg">
        <div className="flex items-start justify-between gap-3 border-b border-border-soft px-4 py-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-text-muted">
              Registered Players
            </div>
            <div className="truncate text-base font-semibold text-text-primary">{tournament.title}</div>
            <div className="mt-1 text-xs text-text-secondary">
              {tournament.registered_count}/{tournament.max_players} seats
            </div>
          </div>
          <Tooltip content="Close">
            <button
              type="button"
              onClick={onClose}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border-soft text-text-muted transition hover:bg-surface hover:text-text-primary"
              aria-label="Close roster"
            >
              <LuX size={17} />
            </button>
          </Tooltip>
        </div>

        <div className="max-h-[60dvh] overflow-y-auto p-3 tron-scrollbar-dark">
          {registrations.length === 0 ? (
            <div className="grid min-h-28 place-items-center rounded-lg border border-border-soft bg-slate-950/35 px-4 text-center text-sm text-text-faint">
              No registered players yet.
            </div>
          ) : (
            <div className="space-y-2">
              {registrations.map((registration, idx) => (
                <div
                  key={registration.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border-soft bg-slate-950/45 px-3 py-2"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-md bg-brand-cyan/10 text-xs font-bold text-brand-cyan">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-text-primary">
                        {registration.name}
                      </div>
                      <div className="text-xs capitalize text-text-muted">{registration.status}</div>
                    </div>
                  </div>

                  {canManage && registration.status === "registered" && !registration.is_me ? (
                    <Tooltip content="Remove">
                      <button
                        type="button"
                        onClick={() => onRemove(registration.id)}
                        disabled={busy}
                        className="grid h-8 w-8 shrink-0 place-items-center rounded-lg border border-brand-rose/20 text-brand-rose transition hover:bg-brand-rose/10 disabled:opacity-50"
                        aria-label={`Remove ${registration.name}`}
                      >
                        <LuTrash2 size={14} />
                      </button>
                    </Tooltip>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
